import { useRef } from "react";
import { useDrawingUtils } from "./useDrawingUtils";

type Point = { x: number; y: number };

type Args = {
  canvasRefs: React.MutableRefObject<Record<number, HTMLCanvasElement | null>>;
  previewCanvasRefs: React.MutableRefObject<
    Record<number, HTMLCanvasElement | null>
  >;
  toolRef: React.MutableRefObject<string>;
  sizeRef: React.MutableRefObject<number>;
  colorRef: React.MutableRefObject<{ hex: string }>;
  zoom: number;
  pan: Point;
  pushState: (page: number) => void;
};

export function useDrawing({
  canvasRefs,
  previewCanvasRefs,
  toolRef,
  sizeRef,
  colorRef,
  zoom,
  pushState,
}: Args) {
  const drawing = useRef(false);
  const penPoints = useRef<Point[]>([]);
  const shapeStart = useRef<Point | null>(null);
  const shapeEnd = useRef<Point | null>(null);
  const activePage = useRef<number | null>(null);
  const { getPos, getCtx, floodFill } = useDrawingUtils();
  const dpr = window.devicePixelRatio || 1;

  const drawingPointerId = useRef<number | null>(null);

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const page = Number(e.currentTarget.dataset.page);

    drawing.current = false;
    drawingPointerId.current = null;
    penPoints.current = [];
    shapeStart.current = null;
    shapeEnd.current = null;
    activePage.current = null;

    if (e.pointerType === "mouse" && e.button !== 0) return;

    const tool = toolRef.current;
    const pos = getPos(e, zoom);

    activePage.current = page;
    drawing.current = true;
    drawingPointerId.current = e.pointerId;

    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);

    if (tool === "pen" || tool === "eraser" || tool === "line" || tool === "rectangle" || tool === "highlight-rect" || tool==="fill") {
      pushState(page);
    }

    if (tool === "pen" || tool === "eraser") {
      penPoints.current = [pos];
      return;
    }
    if (tool === "fill") {
      const pack = getCtx(canvasRefs, page);
      if (!pack) return;

      // IMPORTANT: getPos returns CSS-space coords, but ImageData uses device pixels.
      const dpr = window.devicePixelRatio || 1;
      const sx = Math.floor(pos.x * dpr);
      const sy = Math.floor(pos.y * dpr);

      floodFill(
        pack.ctx,
        sx,
        sy,
        colorRef.current.hex,
        pack.canvas.width,
        pack.canvas.height,
      );
      pushState(page);

      // end immediately (no drag)
      drawing.current = false;
      drawingPointerId.current = null;
      activePage.current = null;
      return;
    }

    shapeStart.current = pos;
    shapeEnd.current = pos;
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (
      !drawing.current ||
      drawingPointerId.current !== e.pointerId ||
      activePage.current == null
    ) {
      return;
    }

    const page = activePage.current;
    const tool = toolRef.current;
    const pos = getPos(e, zoom);

    const pack = getCtx(canvasRefs, page);
    if (!pack) return;

    if (tool === "pen" || tool === "eraser") {
      const { ctx } = pack;
      const last = penPoints.current.at(-1);
      if (!last) return;

      ctx.save();
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = sizeRef.current;
      ctx.globalCompositeOperation =
        tool === "eraser" ? "destination-out" : "source-over";
      ctx.strokeStyle = colorRef.current.hex;

      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      ctx.restore();

      penPoints.current.push(pos);
      return;
    }

    shapeEnd.current = pos;

    const preview = previewCanvasRefs.current[page];
    const pctx = preview?.getContext("2d");
    if (!pctx || !preview || !shapeStart.current) return;

    const x = shapeStart.current.x;
    const y = shapeStart.current.y;
    const w = pos.x - x;
    const h = pos.y - y;

    const cssW = preview.width / dpr;
    const cssH = preview.height / dpr;
    pctx.clearRect(0, 0, cssW, cssH);
    pctx.save();

    if (tool === "highlight-rect") {
      pctx.globalAlpha = 0.25;
      pctx.fillStyle = colorRef.current.hex;
      pctx.fillRect(x, y, w, h);
    } else {
      pctx.lineWidth = sizeRef.current;
      pctx.strokeStyle = colorRef.current.hex;

      if (tool === "line") {
        pctx.beginPath();
        pctx.moveTo(x, y);
        pctx.lineTo(pos.x, pos.y);
        pctx.stroke();
      } else if (tool === "rectangle") {
        pctx.strokeRect(x, y, w, h);
      }
    }

    pctx.restore();
  };

  const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (
      !drawing.current ||
      drawingPointerId.current !== e.pointerId ||
      activePage.current == null
    ) {
      return;
    }

    drawing.current = false;
    drawingPointerId.current = null;

    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {}

    const page = activePage.current;
    const tool = toolRef.current;

    drawing.current = false;
    activePage.current = null;

    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {}

    if (tool === "pen" || tool === "eraser") {
      pushState(page);
      penPoints.current = [];
      return;
    }

    const pack = getCtx(canvasRefs, page);
    if (!pack || !shapeStart.current || !shapeEnd.current) {
      shapeStart.current = null;
      shapeEnd.current = null;
      return;
    }

    const { ctx, canvas } = pack;

    const x = shapeStart.current.x;
    const y = shapeStart.current.y;
    const w = shapeEnd.current.x - x;
    const h = shapeEnd.current.y - y;

    ctx.save();

    if (tool === "highlight-rect") {
      ctx.globalAlpha = 0.25;
      ctx.fillStyle = colorRef.current.hex;
      ctx.fillRect(x, y, w, h);
    } else if (tool === "line") {
      ctx.globalAlpha = 1;
      ctx.lineWidth = sizeRef.current;
      ctx.strokeStyle = colorRef.current.hex;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(shapeEnd.current.x, shapeEnd.current.y);
      ctx.stroke();
    } else if (tool === "rectangle") {
      ctx.globalAlpha = 1;
      ctx.lineWidth = sizeRef.current;
      ctx.strokeStyle = colorRef.current.hex;
      ctx.strokeRect(x, y, w, h);
    }

    ctx.restore();
    pushState(page);

    const preview = previewCanvasRefs.current[page];
    const pctx = preview?.getContext("2d");
    if (pctx && preview) {
      pctx.clearRect(0, 0, preview.width / dpr, preview.height / dpr);
    }

    shapeStart.current = null;
    shapeEnd.current = null;
  };

  return { onPointerDown, onPointerMove, onPointerUp };
}
